import { gql, useMutation, type FetchResult } from "@apollo/client";

import type {
  UpdateModelAlertSettingsInput,
  UpdateModelAlertSettingsMutation,
  UpdateModelAlertSettingsMutationVariables,
} from "../../../API";
import { UPDATE_MODEL_ALERT_SETTINGS } from "../../../common/operations/mutations";
import { handleApolloError } from "../../../common/utils/handleApolloError";
import {
  deviceDataVariable,
  useDeviceDataState,
} from "../../devices/variables/devices";
import { successNotification } from "../../../common/variables/notification";

interface UpdateModelAlertSettingsInterface {
  updateModelAlertSettings: (
    input: UpdateModelAlertSettingsInput
  ) => Promise<FetchResult<UpdateModelAlertSettingsMutation>>;
  data?: UpdateModelAlertSettingsMutation | null;
  loading: boolean;
}

const MAS_FRAGMENT = gql`
  fragment MasFragment on ModelAlertSettings {
    __typename
    id
    serviceId
    customerId
    deviceId
    modelAlertParameters {
      __typename
      enabled
      objectType
      activeDays
      schedules {
        __typename
        dayOfWeek
        dailyTimeRange {
          __typename
          startMinutesFromMidnight
          endMinutesFromMidnight
        }
      }
    }
  }
`;

export const useUpdateModelAlertSettings =
  (): UpdateModelAlertSettingsInterface => {
    const device = useDeviceDataState();

    const [putModelAlertSettings, { data, loading }] = useMutation<
      UpdateModelAlertSettingsMutation,
      UpdateModelAlertSettingsMutationVariables
    >(UPDATE_MODEL_ALERT_SETTINGS, {
      onError: handleApolloError,
      update(cache, { data }) {
        const updated = data?.updateModelAlertSettings;

        if (!updated) return;

        const masId = cache.identify({
          __typename: "ModelAlertSettings",
          id: updated.id,
        });

        if (masId) {
          cache.writeFragment({
            id: masId,
            fragment: MAS_FRAGMENT,
            data: updated,
          });
        }

        if (device?.models) {
          const updatedModel = device.models.map(model =>
            model.serviceId === updated.serviceId
              ? { ...model, modelAlertSettings: updated }
              : model
          );

          deviceDataVariable({
            ...device,
            models: updatedModel,
          });
        }

        successNotification("Model settings updated successfully");
      },
    });

    const updateModelAlertSettings = async (
      input: UpdateModelAlertSettingsInput
    ): Promise<FetchResult<UpdateModelAlertSettingsMutation>> => {
      return await putModelAlertSettings({
        variables: {
          input: {
            ...input,
          },
        },
        refetchQueries: [],
        awaitRefetchQueries: false,
        context: { disableFreshnessLink: true },
      });
    };

    return { updateModelAlertSettings, data, loading };
  };
